import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Select,
  Spinner,
  Stack,
  Text,
  VStack,
  useBreakpointValue,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { sortItems } from "../../../common/usePaginatedList";
import { getNetworkReportFactors, getNetworkReportOrganizations } from "../networkReportApi";
import FactorRankingCardList from "../components/FactorRankingCardList";
import FactorRankingTable from "../components/FactorRankingTable";
import NetworkFactorHighlights from "../components/NetworkFactorHighlights";

const AREA_FILTERS = [
  { value: "all", label: "All" },
  { value: "input", label: "Input" },
  { value: "processes", label: "Processes" },
  { value: "culture_and_climate", label: "Culture & Climate" },
];

const NetworkReportFactorsTab = () => {
  const { networkId } = useParams();
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedColor = useColorModeValue("gray.600", "gray.400");

  const [factors, setFactors] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [areaFilter, setAreaFilter] = useState("all");
  const [sortKey, setSortKey] = useState("rank");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    let active = true;

    const loadFactors = async () => {
      setLoading(true);
      setError("");
      const [factorResult, organizationResult] = await Promise.allSettled([
        getNetworkReportFactors(networkId),
        getNetworkReportOrganizations(networkId),
      ]);

      if (!active) return;
      if (factorResult.status === "rejected") {
        console.error("Error loading network report factors:", factorResult.reason);
        setError("Could not load network report factors.");
        toast({ title: "Could not load network report factors", status: "error", duration: 4000, isClosable: true });
      } else {
        setFactors(factorResult.value);
      }
      setOrganizations(organizationResult.status === "fulfilled" ? organizationResult.value : []);
      setLoading(false);
    };

    loadFactors();
    return () => {
      active = false;
    };
  }, [networkId, toast]);

  const sortOptions = useMemo(() => [
    { key: "rank", label: "Rank", getSortValue: (factor) => factor.rank },
    { key: "factor", label: "Factor", getSortValue: (factor) => factor.name },
    { key: "area", label: "Factor category", getSortValue: (factor) => factor.areaLabel },
    { key: "stability", label: "Stability", getSortValue: (factor) => factor.stabilityIndex ?? -1 },
    { key: "organizations", label: "Schools included", getSortValue: (factor) => factor.organizationsIncluded ?? -1 },
    { key: "respondents", label: "Respondents", getSortValue: (factor) => factor.respondents ?? -1 },
  ], []);

  const filteredFactors = useMemo(() => {
    if (areaFilter === "all") return factors;
    return factors.filter((factor) => factor.areaKey === areaFilter);
  }, [areaFilter, factors]);

  const sortedFactors = useMemo(
    () => sortItems(filteredFactors, sortKey, sortDir, sortOptions),
    [filteredFactors, sortDir, sortKey, sortOptions]
  );

  if (loading) {
    return <HStack justify="center" py={12}><Spinner size="lg" label="Loading network report factors" /></HStack>;
  }

  if (error) {
    return <Alert status="error" borderRadius="md"><AlertIcon />{error}</Alert>;
  }

  return (
    <VStack align="stretch" spacing={4}>
      <Box>
        <Heading size="md">Factors</Heading>
        <Text mt={1} color={mutedColor}>
          Review network-level factor rankings across schools.
        </Text>
      </Box>

      <NetworkFactorHighlights factors={factors} organizations={organizations} />

      <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
        <CardBody>
          <Stack
            direction={{ base: "column", lg: "row" }}
            spacing={4}
            align={{ base: "stretch", lg: "end" }}
            justify="space-between"
          >
            <HStack spacing={4} align="end" flexWrap="wrap">
              <FormControl w={{ base: "100%", md: "180px" }}>
                <FormLabel mb={1}>Factor category</FormLabel>
                <Select value={areaFilter} onChange={(event) => setAreaFilter(event.target.value)}>
                  {AREA_FILTERS.map((filter) => (
                    <option key={filter.value} value={filter.value}>{filter.label}</option>
                  ))}
                </Select>
              </FormControl>

              <FormControl w={{ base: "calc(50% - 8px)", md: "220px" }} minW="180px">
                <FormLabel mb={1}>Sort by</FormLabel>
                <Select value={sortKey} onChange={(event) => setSortKey(event.target.value)}>
                  {sortOptions.map((option) => (
                    <option key={option.key} value={option.key}>{option.label}</option>
                  ))}
                </Select>
              </FormControl>

              <FormControl w={{ base: "calc(50% - 8px)", md: "140px" }} minW="140px">
                <FormLabel mb={1}>Direction</FormLabel>
                <Select value={sortDir} onChange={(event) => setSortDir(event.target.value)}>
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </Select>
              </FormControl>
            </HStack>

            <Text fontSize="sm" color="gray.500">
              {sortedFactors.length} factor{sortedFactors.length === 1 ? "" : "s"}
            </Text>
          </Stack>
        </CardBody>
      </Card>

      {factors.length === 0 ? (
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Heading size="sm" mb={2}>No factors</Heading>
            <Text color={mutedColor}>No factor ranking data is available for this network yet.</Text>
          </CardBody>
        </Card>
      ) : sortedFactors.length === 0 ? (
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Heading size="sm" mb={2}>No factors</Heading>
            <Text color={mutedColor}>No factors match the selected area.</Text>
          </CardBody>
        </Card>
      ) : isMobile ? (
        <FactorRankingCardList factors={sortedFactors} />
      ) : (
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <FactorRankingTable factors={sortedFactors} />
          </CardBody>
        </Card>
      )}
    </VStack>
  );
};

export default NetworkReportFactorsTab;
